import React, { useEffect } from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { StyledContent, StyledHandlers, StyledNewDialog } from '../elements';
import { updateMachineAutomationSystemConfig } from 'redux/machines/actions';
import { IrisV3EssentialsConfigurationSchema } from 'attrs/formValidation';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { getAutomationSystemConfig, getAutomationSystemModbusConfig } from 'redux/machines/selectors';
import {
  loadMachineAutomationSystemConfig,
  loadMachineAutomationSystemModbusConfig
} from '../../../../redux/machines/actions';
import { SUCCESS } from 'attrs/status';
import { getFormikError, getFormikHelperText } from '../../../../helpers/utils';
import { MILL_DRIVE_MODEL_MINISERIES_WAGO_TCP } from '../../../../attrs/sensorConfig';

const EditAutomationSystemConfigurationIRISV3Essentials = ({ open, handleClose, machineId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: automationSystemConfig, loadingState: automationSystemConfigLoadingState } =
    useSelector(getAutomationSystemConfig);

  const { data: automationSystemModbusConfig, loadingState: automationSystemModbusConfigLoadingState } = useSelector(
    getAutomationSystemModbusConfig
  );

  useEffect(() => {
    dispatch(loadMachineAutomationSystemConfig(machineId));
    dispatch(loadMachineAutomationSystemModbusConfig());
  }, [dispatch, machineId]);

  const handleUpdateAutomationSystemConfiguration = values => {
    const iris_v3_essentials_payload = {
      iris_v3_essentials: {
        mill_drive: {
          model: values.mill_drive_model,
          id: String(values.mill_drive_id)
        },
        feeding_pump_drive: {
          model: values.feeding_pump_drive_model,
          id: values.feeding_pump_drive_model === 'NONE' ? 0 : Number(values.feeding_pump_drive_id)
        }
      }
    };

    dispatch(updateMachineAutomationSystemConfig(machineId, iris_v3_essentials_payload));
    handleClose();
  };

  const feedingPumpDriveModelNONE = 'NONE';

  if (
    automationSystemConfigLoadingState.status !== SUCCESS ||
    automationSystemModbusConfigLoadingState.status !== SUCCESS
  )
    return null;

  return (
    <Formik
      initialValues={{
        mill_drive_model: automationSystemConfig.iris_v3_essentials.mill_drive.model,
        mill_drive_id: automationSystemConfig.iris_v3_essentials.mill_drive.id,
        feeding_pump_drive_model: automationSystemConfig.iris_v3_essentials.feeding_pump_drive.model,
        feeding_pump_drive_id: automationSystemConfig.iris_v3_essentials.feeding_pump_drive.id || 0
      }}
      validationSchema={IrisV3EssentialsConfigurationSchema}
      onSubmit={handleUpdateAutomationSystemConfiguration}
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, handleSubmit, errors, touched, resetForm }) => {
        const getHelperText = getFormikHelperText({ errors, t });
        const getError = getFormikError({ errors, touched });

        const actionBar = (
          <StyledHandlers>
            <Button
              color="secondary"
              onClick={() => {
                handleClose();
                resetForm();
              }}
            >
              {t('form.cancel')}
            </Button>
            <Button
              variant="contained"
              style={{ padding: '1rem 2.5rem' }}
              onClick={handleSubmit}
              disabled={
                errors.mill_drive_model ||
                errors.mill_drive_id ||
                errors.feeding_pump_drive_model ||
                errors.feeding_pump_drive_id
              }
            >
              {t('form.save')}
            </Button>
          </StyledHandlers>
        );

        return (
          <StyledNewDialog
            open={open}
            handleClose={(event, reason) => {
              if (reason && reason === 'backdropClick') return;
              handleClose();
              resetForm();
            }}
            title={t('machines.detail.automation_system.configuration.edit_automation_system_configuration')}
            subTitle={t('machines.detail.automation_system.configuration.subtitle')}
            maxWidth="1062px"
            displayActionBar="true"
            actionBar={actionBar}
            content={
              <StyledContent>
                <form>
                  <Grid container spacing={2} columns={12} marginBottom={2}>
                    <Grid item xs={6}>
                      <FormControl variant="filled" required fullWidth>
                        <InputLabel id="automation-system-essentials-mill-drive-model-label">
                          {t('machines.detail.automation_system.configuration.mill_drive_model')}
                        </InputLabel>
                        <Select
                          fullWidth
                          labelId="automation-system-essentials-mill-drive-model-label"
                          id="automation-system-essentials-mill-drive-model"
                          name="mill_drive_model"
                          value={values.mill_drive_model}
                          onChange={handleChange}
                        >
                          {automationSystemModbusConfig.mill.map(item => (
                            <MenuItem key={item.enumeration} value={item.enumeration}>
                              {item.display_value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        required
                        fullWidth
                        type="text"
                        InputProps={
                          values.mill_drive_model === MILL_DRIVE_MODEL_MINISERIES_WAGO_TCP
                            ? undefined
                            : { inputProps: { min: 0 } }
                        }
                        id="mill_drive_id"
                        name="mill_drive_id"
                        label={t(
                          values.mill_drive_model === MILL_DRIVE_MODEL_MINISERIES_WAGO_TCP
                            ? 'machines.detail.automation_system.configuration.mill_address_and_port'
                            : 'machines.detail.automation_system.configuration.mill_id'
                        )}
                        variant="filled"
                        value={values.mill_drive_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={getError('mill_drive_id')}
                        helperText={getHelperText('mill_drive_id', {
                          field: t('machines.detail.automation_system.configuration.feeding_pump_id')
                        })}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} columns={12}>
                    <Grid item xs={6}>
                      <FormControl variant="filled" required fullWidth>
                        <InputLabel id="automation-system-essentials-feeding-pump-label">
                          {t('machines.detail.automation_system.configuration.feeding_pump_drive_model')}
                        </InputLabel>
                        <Select
                          fullWidth
                          labelId="automation-system-essentials-feeding-pump-label"
                          id="automation-system-essentials-feeding-pump"
                          name="feeding_pump_drive_model"
                          value={values.feeding_pump_drive_model}
                          onChange={handleChange}
                        >
                          {automationSystemModbusConfig.feeding_pump.map(item => (
                            <MenuItem key={item.enumeration} value={item.enumeration}>
                              {item.display_value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        required
                        fullWidth
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        id="feeding_pump_drive_id"
                        name="feeding_pump_drive_id"
                        label={t('machines.detail.automation_system.configuration.feeding_pump_id')}
                        variant="filled"
                        value={
                          values.feeding_pump_drive_model === feedingPumpDriveModelNONE
                            ? 0
                            : values.feeding_pump_drive_id
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={getError('feeding_pump_drive_id')}
                        helperText={getHelperText('feeding_pump_drive_id', {
                          field: t('machines.detail.automation_system.configuration.mill_id')
                        })}
                        disabled={values.feeding_pump_drive_model === feedingPumpDriveModelNONE}
                      />
                    </Grid>
                  </Grid>
                </form>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

EditAutomationSystemConfigurationIRISV3Essentials.propTypes = {
  open: T.bool.isRequired,
  handleClose: T.func.isRequired,
  machineId: T.string.isRequired,
  automationSystem: T.shape({
    plc_type: T.string
  }).isRequired
};

export default EditAutomationSystemConfigurationIRISV3Essentials;
